import React, { useCallback, useEffect, useState } from "react";
import { Button, Table, Tag, Modal, Input, Popconfirm, Tooltip, Switch, message } from "antd";
import {
  PlusOutlined, MailOutlined, KeyOutlined, UserDeleteOutlined, ReloadOutlined,
  CheckCircleTwoTone, ClockCircleOutlined,
} from "@ant-design/icons";
import * as api from "./controller";
import "./crm.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;
const errmsg = (r, f) => (r && r.json && r.json.detail) || f;

/** Super-admin per-office user management, embedded in the Office detail drawer.
 * Reuses the /clinics/office/<id>/users/ endpoints (a superuser passes the gate). */
export default function OfficeUsersPanel({ officeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);

  const load = useCallback(async () => {
    if (!officeId) return;
    setLoading(true);
    try {
      const r = await api.getOfficeUsers(officeId);
      setRows(ok(r) && Array.isArray(r.json) ? r.json : []);
    } finally {
      setLoading(false);
    }
  }, [officeId]);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (row, active) => {
    const r = await api.setUserActive(row.id, active);
    if (ok(r)) { message.success(active ? "Reactivated." : "Deactivated."); load(); }
    else message.error(errmsg(r, "Couldn't update the account."));
  };
  const remove = async (row) => {
    const r = await api.removeOfficeUser(officeId, row.id);
    if (ok(r)) { message.success("Removed from office."); load(); }
    else message.error(errmsg(r, "Couldn't remove the user."));
  };

  const columns = [
    {
      title: "User",
      render: (_, r) => (
        <div className="crm-cell-stack">
          <div className="crm-name">{r.name || r.email}</div>
          <div className="crm-sub">{r.email}</div>
        </div>
      ),
    },
    {
      title: "Active", width: 84, align: "center",
      render: (_, r) => (
        <Switch size="small" checked={r.is_active} onChange={(v) => toggleActive(r, v)} />
      ),
    },
    {
      title: "Verified", width: 78, align: "center",
      render: (_, r) => r.email_verified
        ? <Tooltip title="Can log in"><CheckCircleTwoTone twoToneColor="#52c41a" /></Tooltip>
        : <Tooltip title="Invited — not yet signed up"><ClockCircleOutlined style={{ color: "#faad14" }} /></Tooltip>,
    },
    { title: "Access", width: 96, render: (_, r) => (r.removable ? <Tag>Member</Tag> : <Tag color="gold">Owner</Tag>) },
    {
      title: "", key: "a", align: "right", width: 210,
      render: (_, r) => (
        <div className="crm-row-actions">
          <Button size="small" icon={<KeyOutlined />} onClick={() => setPwUser(r)}>Password</Button>
          {r.removable ? (
            <Popconfirm title="Remove from this office?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove(r)}>
              <Button size="small" danger icon={<UserDeleteOutlined />} />
            </Popconfirm>
          ) : (
            <Tooltip title="Owner access can't be removed here"><Button size="small" icon={<UserDeleteOutlined />} disabled /></Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="crm-row-actions" style={{ justifyContent: "flex-end", marginBottom: 10 }}>
        <Button size="small" icon={<MailOutlined />} onClick={() => setInviteOpen(true)}>Invite</Button>
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>Add user</Button>
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
      </div>
      <Table size="small" rowKey="id" columns={columns} dataSource={rows} loading={loading}
        pagination={false} locale={{ emptyText: <div className="crm-empty">No users in this office.</div> }} />

      <EmailPwModal open={createOpen} title="Add user to office" okText="Create" withPassword
        onClose={() => setCreateOpen(false)}
        onSubmit={(email, password) => api.createOfficeUser(officeId, email, password)}
        onDone={() => { setCreateOpen(false); load(); }} />
      <EmailPwModal open={inviteOpen} title="Invite user to office" okText="Send invite"
        onClose={() => setInviteOpen(false)}
        onSubmit={(email) => api.inviteOfficeUser(officeId, email)}
        onDone={() => setInviteOpen(false)} />
      <EmailPwModal open={!!pwUser} title={pwUser ? `Set password — ${pwUser.email}` : ""} okText="Update password"
        withPassword passwordOnly
        onClose={() => setPwUser(null)}
        onSubmit={(_e, password) => api.setOfficeUserPassword(officeId, pwUser.id, password)}
        onDone={() => setPwUser(null)} />
    </div>
  );
}

/** Small shared modal: email (+optional password), or password-only for reset. */
function EmailPwModal({ open, title, okText, withPassword, passwordOnly, onClose, onSubmit, onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setEmail(""); setPassword(""); } }, [open]);

  const submit = async () => {
    if (!passwordOnly && !email.trim()) { message.error("Email is required."); return; }
    if (withPassword && !password) { message.error("Password is required."); return; }
    setBusy(true);
    const r = await onSubmit(email.trim(), password);
    setBusy(false);
    if (ok(r)) {
      message.success(r.json && r.json.email_sent === false ? "Created (invite email couldn't be sent)." : "Done.");
      onDone();
    } else {
      message.error(errmsg(r, "Something went wrong."));
    }
  };

  return (
    <Modal title={title} open={open} onCancel={onClose} onOk={submit} okText={okText}
      confirmLoading={busy} zIndex={10050} destroyOnClose>
      {!passwordOnly ? (
        <>
          <label className="crm-field-label">Email</label>
          <Input placeholder="person@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        </>
      ) : null}
      {withPassword ? (
        <>
          <label className="crm-field-label" style={{ marginTop: passwordOnly ? 0 : 12 }}>
            {passwordOnly ? "New password" : "Temporary password"}
          </label>
          <Input.Password autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </>
      ) : null}
    </Modal>
  );
}
